import {toTwoDecimals} from '../../lib/payments';

export interface CurrencyFormatOptions {
    useCommas: boolean;
    useEuropean: boolean;
}

export interface CurrencyEditOptions {
    useEuropean: boolean;
    /** Accept a leading minus sign. */
    allowNegative: boolean;
}

/**
 * Format a money amount for display with two decimals. Rounds through the payments WASM helper
 * (half-up on the decimal value), never `Math.round(n * 100) / 100` (OWA-451).
 */
export function formatCurrencyValue(number: number, {useCommas, useEuropean}: CurrencyFormatOptions): string {
    if (number === null || number === undefined || isNaN(number)) {
        return '';
    }

    const roundedNumber = toTwoDecimals(number);
    const formattedNumber = roundedNumber.toFixed(2);
    let [wholePart, decimalPart] = formattedNumber.split('.');

    if (useCommas) {
        const grouped = new Intl.NumberFormat('en-US').format(parseInt(wholePart));
        wholePart = useEuropean ? grouped.replace(/,/g, '.') : grouped;
    }

    return useEuropean ? `${wholePart},${decimalPart}` : `${wholePart}.${decimalPart}`;
}

/**
 * Reduce what the user typed to the characters a money field may hold: digits, at most one
 * decimal character, and (when allowed) a single leading minus sign.
 */
export function sanitizeCurrencyEdit(rawInput: string, {useEuropean, allowNegative}: CurrencyEditOptions): string {
    const decimalChar = useEuropean ? ',' : '.';
    const negative = allowNegative && rawInput.trim().startsWith('-');
    let kept = '';
    for (const ch of rawInput) {
        if ((ch >= '0' && ch <= '9') || ch === decimalChar) kept += ch;
    }

    const parts = kept.split(decimalChar);
    const processed = parts.length > 1 ? `${parts[0]}${decimalChar}${parts.slice(1).join('')}` : parts[0];
    return negative ? `-${processed}` : processed;
}

/** Turn an edited string (possibly with symbols or thousands separators) into a plain numeric string. */
export function currencyEditToNumericString(inputStr: string, useEuropean: boolean): string {
    if (!inputStr) return '';

    let cleanStr = inputStr;
    cleanStr = cleanStr.replace(/[A-Za-z]/g, '');
    cleanStr = cleanStr.replace(/[$€£¥₹₽₺₴₩₫₦₲₸₼₾₿฿]/g, '').replace(/\s/g, '');

    if (useEuropean) {
        cleanStr = cleanStr.replace(/\./g, '').replace(/,/g, '.');
    } else {
        cleanStr = cleanStr.replace(/,/g, '');
    }

    return cleanStr;
}
